import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Eye, Network, Power, Phone, Lock, Loader2, BarChart3, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';

const ServicesAndPricing = () => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('electrical');
  const [propertyType, setPropertyType] = useState('residential');
  const [showEstimator, setShowEstimator] = useState(false);
  const [estimateDescription, setEstimateDescription] = useState('');
  const [estimateAddress, setEstimateAddress] = useState('');
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);

  useEffect(() => { loadCatalog(); }, []);

  const loadCatalog = async () => {
    try { const data = await api('/api/services/catalog'); setCatalog(data); }
    catch (err) { toast.error('Failed to load service catalog'); }
    finally { setLoading(false); }
  };

  const getAIEstimate = async () => {
    if (!estimateDescription.trim()) { toast.error('Please describe what you need'); return; }
    setEstimating(true); setEstimate(null);
    try {
      const data = await api('/api/services/estimate', {
        method: 'POST',
        body: JSON.stringify({ description: estimateDescription, property_type: propertyType, address: estimateAddress || 'New York City' })
      });
      setEstimate(data);
    } catch (err) { toast.error(err.message || 'Failed to generate estimate'); }
    finally { setEstimating(false); }
  };

  const categoryIcons = { electrical: Zap, cctv: Eye, networking: Network, generator: Power, intercom: Phone, access_control: Lock };

  if (loading) return <LoadingSpinner />;

  const currentCategory = catalog?.services?.[selectedCategory];
  const currentServices = currentCategory?.[propertyType] || {};

  return (
    <div data-testid="services-pricing" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Services & Pricing</h1>
          <p className="text-gray-400 mt-1">NYC Professional Rates - Licensed & Insured</p>
        </div>
        <button onClick={() => setShowEstimator(!showEstimator)} className="btn-primary flex items-center gap-2" data-testid="ai-estimate-btn">
          <Zap className="w-5 h-5" />AI Price Estimator
        </button>
      </div>

      {showEstimator && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="card p-6 bg-gradient-to-br from-gray-900 to-gray-800 border-red-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <div>
              <h2 className="font-heading text-xl font-bold text-white">AI-Powered Price Estimator</h2>
              <p className="text-sm text-gray-400">Describe your project and get an instant estimate</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Property Type</label>
              <select className="input" value={propertyType} onChange={(e) => setPropertyType(e.target.value)} data-testid="property-type-select">
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Address (Optional)</label>
              <input type="text" className="input" placeholder="NYC Address" value={estimateAddress} onChange={(e) => setEstimateAddress(e.target.value)} data-testid="estimate-address-input" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Describe Your Project</label>
            <textarea className="input min-h-[120px]" placeholder="Example: I need to install 4 security cameras outside my building..." value={estimateDescription} onChange={(e) => setEstimateDescription(e.target.value)} data-testid="estimate-description-input" />
          </div>
          <button onClick={getAIEstimate} disabled={estimating} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="get-estimate-btn">
            {estimating ? (<><Loader2 className="w-5 h-5 animate-spin" />Analyzing Your Project...</>) : (<><BarChart3 className="w-5 h-5" />Get AI Estimate</>)}
          </button>

          {estimate && !estimate.error && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-6 bg-black/50 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold text-white">Your Estimate</h3>
                <span className="badge bg-green-600 text-white">{estimate.property_type?.toUpperCase()}</span>
              </div>
              {estimate.summary && <p className="text-gray-300 mb-4">{estimate.summary}</p>}
              {estimate.line_items && estimate.line_items.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wider">Line Items</h4>
                  <div className="space-y-2">
                    {estimate.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-700">
                        <div><span className="text-white">{item.item}</span>{item.quantity > 1 && <span className="text-gray-500 text-sm ml-2">x{item.quantity}</span>}</div>
                        <span className="text-white font-mono">${item.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2 pt-4 border-t border-gray-600">
                {estimate.subtotal && <div className="flex justify-between text-gray-300"><span>Subtotal</span><span className="font-mono">${estimate.subtotal?.toLocaleString()}</span></div>}
                {estimate.permits_total > 0 && <div className="flex justify-between text-gray-300"><span>Permits & Fees</span><span className="font-mono">${estimate.permits_total?.toLocaleString()}</span></div>}
                {estimate.contingency > 0 && <div className="flex justify-between text-gray-300"><span>Contingency (10%)</span><span className="font-mono">${estimate.contingency?.toLocaleString()}</span></div>}
                {estimate.tax > 0 && <div className="flex justify-between text-gray-300"><span>NYC Sales Tax (8.875%)</span><span className="font-mono">${estimate.tax?.toLocaleString()}</span></div>}
                <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-gray-600">
                  <span>Total Estimate</span><span className="text-red-500 font-mono">${estimate.grand_total?.toLocaleString()}</span>
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {estimate.estimated_duration && <div className="p-3 bg-gray-800 rounded"><span className="text-gray-400 text-sm">Estimated Duration</span><p className="text-white font-semibold">{estimate.estimated_duration}</p></div>}
                {estimate.warranty && <div className="p-3 bg-gray-800 rounded"><span className="text-gray-400 text-sm">Warranty</span><p className="text-white font-semibold">{estimate.warranty}</p></div>}
              </div>
              {estimate.notes && estimate.notes.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded">
                  <h4 className="text-yellow-500 text-sm font-semibold mb-2">Important Notes</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {estimate.notes.map((note, idx) => (<li key={idx} className="flex items-start gap-2"><AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />{note}</li>))}
                  </ul>
                </div>
              )}
              <div className="mt-4 flex gap-3">
                <Link to="/customer/new-request" className="btn-primary flex-1 text-center">Request This Service</Link>
                <button onClick={() => setEstimate(null)} className="btn-secondary">New Estimate</button>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      <div className="flex items-center gap-4">
        <span className="text-gray-400 text-sm">Property Type:</span>
        <div className="flex bg-gray-800 rounded-lg p-1">
          <button onClick={() => setPropertyType('residential')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${propertyType === 'residential' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-testid="residential-btn">Residential</button>
          <button onClick={() => setPropertyType('commercial')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${propertyType === 'commercial' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-white'}`} data-testid="commercial-btn">Commercial</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {catalog?.services && Object.keys(catalog.services).map((catKey) => {
          const cat = catalog.services[catKey];
          const Icon = categoryIcons[catKey] || Zap;
          const isActive = selectedCategory === catKey;
          return (
            <button key={catKey} onClick={() => setSelectedCategory(catKey)} className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${isActive ? 'bg-red-600 text-white shadow-lg shadow-red-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'}`} data-testid={`category-${catKey}`}>
              <Icon className="w-5 h-5" /><span className="hidden sm:inline">{cat.name}</span>
            </button>
          );
        })}
      </div>

      <div className="card bg-gray-900 border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800 bg-gray-800/50">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            {(() => { const Icon = categoryIcons[selectedCategory] || Zap; return <Icon className="w-6 h-6 text-red-500" />; })()}
            {currentCategory?.name} - {propertyType === 'residential' ? 'Residential' : 'Commercial'} Rates
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">Price</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300 uppercase tracking-wider">Unit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {Object.entries(currentServices).map(([key, service]) => (
                <tr key={key} className="hover:bg-gray-800/50 transition-colors" data-testid={`service-row-${key}`}>
                  <td className="px-4 py-4"><span className="text-white font-medium capitalize">{key.replace(/_/g, ' ')}</span></td>
                  <td className="px-4 py-4 text-gray-400">{service.description}</td>
                  <td className="px-4 py-4 text-right"><span className="text-xl font-bold text-red-500 font-mono">${service.price.toLocaleString()}</span></td>
                  <td className="px-4 py-4 text-center"><span className="badge bg-gray-700 text-gray-300">{service.unit}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {catalog?.permits && (
        <div className="card bg-gray-900 border-gray-800 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-red-500" />NYC Permits & Inspection Fees</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.entries(catalog.permits).map(([key, permit]) => (
              <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded">
                <span className="text-gray-300">{permit.description}</span>
                <span className="text-white font-bold font-mono">${permit.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {catalog?.notes && (
        <div className="card bg-gray-900 border-gray-800 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Important Information</h3>
          <div className="space-y-3 text-sm text-gray-400">
            <p><strong className="text-gray-300">Disclaimer:</strong> {catalog.notes.disclaimer}</p>
            <p><strong className="text-gray-300">Warranty:</strong> {catalog.notes.warranty}</p>
            <p><strong className="text-gray-300">Licenses:</strong> {catalog.notes.licenses}</p>
            <p><strong className="text-gray-300">Payment:</strong> {catalog.notes.payment}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesAndPricing;
